import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { achievementService, MyAchievementEntry, authService, User } from '../services/api';
import { achievementCircleColor } from '../utils/achievementRarity';
import { formatAchievementRarity } from '../utils/formatAchievementRarity';
import { achievementIconSrcFromKey } from '../utils/achievementIconAsset';
import './ProfileAchievementsSection.css';
import './AchievementsPage.css';

const AchievementsPage: React.FC = () => {
  const { t, ready, i18n } = useSafeTranslation();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const isPublicView = Boolean(userId);

  const [items, setItems] = useState<MyAchievementEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'auth' | 'load' | null>(null);
  const [viewer, setViewer] = useState<User | null>(null);

  const loadViewer = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const u = await authService.getProfile();
      setViewer(u);
    } catch {
      setViewer(null);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isPublicView) {
        const data = await achievementService.getPublicUnlocked(userId!);
        setItems(data);
      } else {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('auth');
          setItems([]);
          return;
        }
        const data = await achievementService.getMine();
        setItems(data);
      }
    } catch {
      setError('load');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isPublicView, userId]);

  useEffect(() => {
    loadViewer();
  }, [loadViewer]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const onLang = () => {
      void loadList();
    };
    i18n.on('languageChanged', onLang);
    return () => {
      i18n.off('languageChanged', onLang);
    };
  }, [i18n, loadList]);

  const title = isPublicView
    ? t('achievements.pageTitlePublic', 'Player achievements')
    : t('achievements.pageTitleMine', 'My achievements');

  return (
    <div className="achievements-page py-4">
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8}>
            <div className="d-flex align-items-center gap-3 mb-4">
              <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
                <i className="bi bi-arrow-left me-1" aria-hidden="true" />
                {t('common.back', 'Back')}
              </Button>
              <h1 id="achievements-page-title" className="h4 mb-0">
                {title}
              </h1>
            </div>

            <section aria-labelledby="achievements-page-title" aria-busy={loading}>
              {error === 'auth' && (
                <Alert variant="warning">
                  {t('achievements.loginToViewYours', 'Sign in to view your achievements.')}
                  <Button variant="link" className="p-0 ms-2" onClick={() => navigate('/')}>
                    {t('navigation.backToHome', 'Back to Home')}
                  </Button>
                </Alert>
              )}

              {error === 'load' && (
                <Alert variant="danger" className="d-flex flex-wrap align-items-center gap-2">
                  <span>{t('achievements.loadError', 'Could not load achievements.')}</span>
                  <Button variant="outline-danger" size="sm" onClick={() => loadList()}>
                    {t('achievements.retryLoad', 'Try again')}
                  </Button>
                </Alert>
              )}

              {loading && (
                <div className="text-center py-5" role="status" aria-live="polite">
                  <Spinner animation="border" />
                  <span className="visually-hidden d-block mt-2">
                    {t('achievements.loadingFullPage', 'Loading achievement list…')}
                  </span>
                </div>
              )}

              {!loading && !error && items.length === 0 && (
                <Card body className="text-muted text-center">
                  {t('achievements.emptyFull', 'No achievements to show.')}
                </Card>
              )}

              {!loading && items.length > 0 && (
                <ul className="list-unstyled achievements-full-list m-0 p-0">
                  {items.map((a) => {
                    const bg = achievementCircleColor(a.rarity);
                    const rarityLabel = formatAchievementRarity(a.rarity, t);
                    const iconSrc = achievementIconSrcFromKey(a.iconAssetKey);
                    const circleAria = ready
                      ? i18n.t('achievements.ariaBadge', {
                          name: a.name,
                          rarity: rarityLabel,
                          points: a.points,
                        })
                      : `${a.name}, ${rarityLabel}, ${a.points} points`;
                    const tooltip = (
                      <Tooltip id={`full-${a.achievementId}`}>
                        <div className="achievement-tooltip-content small">
                          <div className="achievement-tooltip-title">{a.name}</div>
                          {a.description?.trim() ? (
                            <div className="achievement-tooltip-desc">{a.description.trim()}</div>
                          ) : null}
                          <div className="achievement-tooltip-meta">
                            {rarityLabel} · {t('achievements.pointsLabel', 'Points')}: {a.points}
                          </div>
                        </div>
                      </Tooltip>
                    );
                    return (
                      <li key={a.achievementId} className="achievements-full-row card mb-2">
                        <div className="card-body d-flex align-items-center gap-3 py-3">
                          <OverlayTrigger placement="top" trigger={['hover', 'focus']} overlay={tooltip}>
                            <div className="d-flex flex-shrink-0" tabIndex={0}>
                              <div
                                className="profile-achievement-circle d-flex align-items-center justify-content-center"
                                style={{ backgroundColor: bg }}
                                aria-label={circleAria}
                                role="img"
                              >
                                {iconSrc ? (
                                  <img src={iconSrc} alt="" className="profile-achievement-circle-img" />
                                ) : (
                                  <i className="bi bi-trophy-fill profile-achievement-fallback-icon" aria-hidden="true" />
                                )}
                              </div>
                            </div>
                          </OverlayTrigger>
                          <div className="flex-grow-1 min-width-0">
                            <div className="fw-semibold text-truncate">{a.name}</div>
                            <div className="small achievements-row-meta">
                              {rarityLabel} · {a.points} {t('achievements.pointsShort', 'pts')}
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {viewer && !isPublicView && (
                <p className="small text-muted mt-3 mb-0 text-center">
                  {ready
                    ? i18n.t('achievements.loggedInAs', { nick: viewer.nickname || viewer.name })
                    : t('achievements.loggedInAs', 'Signed in as {{nick}}').replace(
                        '{{nick}}',
                        viewer.nickname || viewer.name
                      )}
                </p>
              )}

              <Card className="profile-card mt-4 back-to-home-card">
                <Card.Body className="d-flex justify-content-center py-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    onClick={() => navigate('/')}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    {t('navigation.backToHome', 'Back to Home')}
                  </Button>
                </Card.Body>
              </Card>
            </section>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AchievementsPage;
